import dynamic from 'next/dynamic';
import { MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react'; // Removed useState, added useMemo, Added MouseEvent
import { domainCode } from '../../../../../constants/staticPageConstants';
import { Meta } from '../../../../../layout/Meta';
import { handleTrack } from '../../../../../utils/tracker'; // Import handleTrack
import { Breadcrumb } from '../../../../Atoms/BreadCrumb';
import { ButtonVariants } from '../../../../Atoms/Button';
import { If } from '../../../../Atoms/Conditionals';
import { AuthorCard } from '../../../../Author/AuthorCard';
import { FILE_NOW_TEXT } from '../../../../CTA/constants';
import { FaqComponent } from '../../../../Common/FAQS';
import { checkIfB2CPage } from '../../../../Header/utils';
import { ClearInNavbar } from '../../../../Navbar/ClearNavbar';
import { Heading, HeadingType } from '../../../../Typography';
import {
  CMSLayoutSchema,
  NAV_TYPES,
  TopCTA,
} from '../../../../cms/layout/components';
import {
  CMSHeader,
  b2bData,
  b2cData,
} from '../../../../cms/layout/components/header';
import { useOnMountCMSLayout } from '../../../../cms/layout/hooks/useOnMountCMSLayout';
import { generateLanguageTags } from '../../../../cms/layout/language/languageTags';
import { ISINGLE_POST } from '../../../../cms/layout/types'; // Removed unused IFAQ
import {
  ReusableComponent,
  addLazyLoadAttributesToHtmlString,
  getActiveFAQsInFAQFormat,
  getBreadCrumbData,
  optimiseYoutube,
  stripUnwantedStyles,
} from '../../../../cms/layout/util';
import { OTHER_CALCULATORS, POPULAR_CALCULATORS } from '../../constants';
import { CalculatorLayoutStyle } from '../../styles';
import { getCalculatorPageUrl, renderCalculator } from '../../utils';
import { ArticleList } from '../ArticlesList';
import { Share } from '../Share';

const StickyCTA = dynamic(
  () => import('../../../../CTA/StickyCTA').then((mod) => mod.StickyCTA),
  { suspense: true }
);

export interface ICALCULATOR_LAYOUT {
  postData: {
    data: ISINGLE_POST;
  };
}
const CalculatorLayout = ({ postData }: ICALCULATOR_LAYOUT) => {
  const { data } = postData;
  const calcData = data?.calculatorData?.parameters;
  const myContainer = useRef(null);
  const { CURR_PAGE_URL, LANGUAGE } = getCalculatorPageUrl(data);
  const BREADCRUMB_DATA = getBreadCrumbData(data, LANGUAGE);
  const authorDetails = data.author_details;
  const showAuthor =
    authorDetails.status === true && authorDetails.domain.includes(data.domain);

  // Memoize processed content to add lazy loading and strip styles
  const processedContent = useMemo(() => {
    const strippedContent = stripUnwantedStyles(data.content || '');
    return addLazyLoadAttributesToHtmlString(strippedContent);
  }, [data.content]);

  // Memoize processed FAQs to strip styles
  const processedFaqs = useMemo(() => {
    if (!data.faq) return [];
    const strippedFaqs = data.faq.map((item) => ({
      ...item,
      answer: stripUnwantedStyles(item.answer || ''),
    }));
    return getActiveFAQsInFAQFormat(strippedFaqs);
  }, [data.faq]);

  useEffect(() => {
    const containerElement = myContainer.current as HTMLElement | null;
    if (!containerElement) return;

    // Run DOM-dependent optimizations after content is rendered
    optimiseYoutube(containerElement);
    ReusableComponent(containerElement);

    // Dependency: run when processedContent changes, indicating the underlying HTML structure might have changed
  }, [processedContent]);
  // Define captureEvent (seems okay, just removed the unused setDemoPopup logic if it were here)
  const captureEvent = useCallback((e: MouseEvent<Element, MouseEvent>) => {
    if (!(e?.target instanceof HTMLElement)) {
      return;
    }
    const isLabelRequestADemo = e?.target.dataset.label;

    if (isLabelRequestADemo && isLabelRequestADemo === 'Request a demo') {
      e.preventDefault();
      handleTrack({
        label: 'top_cta',
        obj: e.target,
      });
    } else {
      handleTrack({
        label: 'top_cta',
        obj: e.target,
      });
    }
  }, []);

  useOnMountCMSLayout(data);

  const navData = checkIfB2CPage(data.category.slug) ? b2cData : b2bData;

  return (
    <div>
      {data.domain === domainCode.cleartaxIn ? (
        <ClearInNavbar data={navData} />
      ) : (
        <CMSHeader
          domain={data.domain}
          category={data.category}
          language={LANGUAGE}
          isMultiLang={data.multi_language}
          permalink={data.permalink}
        />
      )}
      <Meta
        title={data.meta_title}
        description={data.description}
        canonical={CURR_PAGE_URL}
        favicon="https://assets1.cleartax-cdn.com/cleartax/images/1644906212_clearfavicon.png"
        hrefLang={generateLanguageTags(data.multi_language, data)}
        noindex={data.no_index}
        img="https://assets.clear.in/cleartax/images/1753522640_cleartaxhomepage_.png"
      ></Meta>
      <div className="sm:mt-5">
        <TopCTA CTA={data.cta || ''} onClickCB={captureEvent} />
      </div>
      <StickyCTA
        buttonText={FILE_NOW_TEXT}
        buttonStyle={ButtonVariants.bluefilled}
        type={NAV_TYPES.B2C}
      />
      <div className="wg-container">
        <Breadcrumb dataSet={BREADCRUMB_DATA} className="pt-6 sm:pt-3" />
        <div className="flex justify-between items-center">
          <div>
            <Heading
              variant={HeadingType?.h1 || ''}
              content={data?.title || ''}
              className="my-12 text-s-32 sm:my-9 sm:text-s-24 text-left"
            />
          </div>
          <Share url={CURR_PAGE_URL} domain={data.domain} language={LANGUAGE} />
        </div>

        {renderCalculator(calcData)}

        <CalculatorLayoutStyle>
          <div ref={myContainer}>
            <div className="flex justify-between gap-6 calculator sm:flex-col">
              <div className="w-8/12 sm:w-full">
                {/* Use the pre-processed content */}
                <div dangerouslySetInnerHTML={{ __html: processedContent }} />

                <FaqComponent
                  FAQS={processedFaqs} // Removed problematic comment
                  isShowSchema={true}
                  variant="partner"
                  faqIconColor={'#314259'}
                  htmlFromApi={true}
                  expanded="false"
                />

                <If condition={showAuthor && !!authorDetails?.description}>
                  <div id="authorBio" className="pt-14">
                    <AuthorCard
                      name={authorDetails.name}
                      designation={authorDetails.designation}
                      authorImg={authorDetails.image_url || ''}
                      description={authorDetails.description}
                      linkedin={authorDetails.linkedin_url}
                      facebook={authorDetails.facebook_url}
                      twitter={authorDetails.twitter_url}
                      slug={authorDetails.slug}
                      showHeading={true}
                    />
                  </div>
                </If>
              </div>
              <If condition={data.domain === domainCode.cleartaxIn}>
                <div className="w-4/12 mt-14 sm:w-full right-articles-section">
                  <div className="flex flex-col gap-8">
                    <ArticleList article={POPULAR_CALCULATORS} />

                    <ArticleList article={OTHER_CALCULATORS} />
                  </div>
                </div>
              </If>
            </div>
          </div>
        </CalculatorLayoutStyle>
      </div>

      <CMSLayoutSchema
        postData={data}
        pageUrl={CURR_PAGE_URL}
        language={LANGUAGE}
      />
    </div>
  );
};

export default CalculatorLayout;
